import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import UndoIcon from "@mui/icons-material/Undo";
import axiosInstanceMerchandising from "../axiosInstanceMerchandising";
import axiosInstanceMerchandisingPreview from "../axiosInstanceMerchandisingPreview";

const MAX_RULES = 20;
const MAX_EXPRESSION_LENGTH = 500;
const MIN_BOOST = -100;
const MAX_BOOST = 100;

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "rgba(255,255,255,0.92)",
    "&.Mui-focused fieldset": { borderColor: "#f97316" },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "#f97316" },
};

const primaryButtonSx = {
  borderRadius: "12px",
  textTransform: "none",
  fontWeight: 700,
  background: "linear-gradient(135deg, #fb923c, #f97316)",
  boxShadow: "none",
  "&:hover": { background: "linear-gradient(135deg, #f97316, #ea580c)", boxShadow: "none" },
};

const neutralButtonSx = {
  borderRadius: "12px",
  textTransform: "none",
  fontWeight: 700,
  color: "#475569",
  borderColor: "rgba(15, 23, 42, 0.14)",
  "&:hover": { borderColor: "rgba(249, 115, 22, 0.45)", backgroundColor: "rgba(255, 247, 241, 0.8)" },
};

const readValue = (value, camelKey, pascalKey) => value?.[camelKey] ?? value?.[pascalKey];

const normalizeRule = (rule = {}) => ({
  expression: String(readValue(rule, "expression", "Expression") ?? ""),
  boost: String(readValue(rule, "boost", "Boost") ?? 1),
  enabled: Boolean(readValue(rule, "enabled", "Enabled") ?? true),
});

const normalizeRules = (rules) => (Array.isArray(rules) ? rules.map(normalizeRule) : []);

const serializeRules = (rules) => rules.map((rule) => ({
  expression: rule.expression.trim(),
  boost: Number(rule.boost),
  enabled: rule.enabled,
}));

const validateRules = (rules) => {
  const errors = rules.map(() => ({}));
  const expressions = new Map();

  rules.forEach((rule, index) => {
    const expression = rule.expression.trim();
    if (!expression) errors[index].expression = "Expression is required.";
    else if (rule.expression.length > MAX_EXPRESSION_LENGTH) {
      errors[index].expression = `Expression cannot exceed ${MAX_EXPRESSION_LENGTH} characters.`;
    } else {
      const key = expression.toLowerCase();
      if (expressions.has(key)) {
        errors[index].expression = `Expression duplicates rule ${expressions.get(key) + 1}.`;
        errors[expressions.get(key)].expression = `Expression duplicates rule ${index + 1}.`;
      } else {
        expressions.set(key, index);
      }
    }

    const boost = Number(rule.boost);
    if (rule.boost === "" || !Number.isFinite(boost) || boost < MIN_BOOST || boost > MAX_BOOST) {
      errors[index].boost = `Enter a number from ${MIN_BOOST} to ${MAX_BOOST}.`;
    }
  });

  return errors;
};

const getErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (typeof data?.message === "string" && data.message.trim()) return data.message;
  if (typeof data?.title === "string" && data.title.trim()) return data.title;
  return fallback;
};

const formatPrice = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? `$${number.toFixed(2)}` : "";
};

const ProductTile = ({ product, rank, rules }) => {
  const id = readValue(product, "id", "Id") || rank;
  const title = readValue(product, "title", "Title") || "Untitled product";
  const vendor = readValue(product, "vendor", "Vendor");
  const productType = readValue(product, "productType", "ProductType");
  const imageUrl = readValue(product, "imageUrl", "ImageUrl");
  const link = readValue(product, "link", "Link");
  const price = formatPrice(readValue(product, "price", "Price"));
  const originalPrice = formatPrice(readValue(product, "originalPrice", "OriginalPrice"));
  const sale = Boolean(readValue(product, "sale", "Sale")) && originalPrice && originalPrice !== price;
  const rankChangeValue = readValue(product, "rankChange", "RankChange");
  const numericRankChange = Number(rankChangeValue);
  const rankChange = rankChangeValue !== null && rankChangeValue !== undefined && Number.isFinite(numericRankChange)
    ? numericRankChange
    : null;
  const rankChangePresentation = rankChange === null ? null : rankChange > 0
    ? {
        label: `Rank +${rankChange}`,
        tooltip: `Promoted ${rankChange} ${rankChange === 1 ? "position" : "positions"}.`,
        color: "#15803d",
        backgroundColor: "rgba(22,163,74,0.12)",
        icon: <ArrowUpwardIcon />,
      }
    : rankChange < 0
      ? {
          label: `Rank ${rankChange}`,
          tooltip: `Demoted ${Math.abs(rankChange)} ${Math.abs(rankChange) === 1 ? "position" : "positions"}.`,
          color: "#b91c1c",
          backgroundColor: "rgba(220,38,38,0.10)",
          icon: <ArrowDownwardIcon />,
        }
      : {
          label: "Rank 0",
          tooltip: "Ranking is unchanged.",
          color: "#64748b",
          backgroundColor: "rgba(100,116,139,0.12)",
          icon: undefined,
        };
  const matchedRulesValue = readValue(product, "matchedRules", "MatchedRules");
  const matchedRules = Array.isArray(matchedRulesValue)
    ? matchedRulesValue.filter((ruleNumber) => Number.isInteger(Number(ruleNumber)) && Number(ruleNumber) > 0).map(Number)
    : [];

  return (
    <Paper
      elevation={0}
      sx={{
        display: "grid",
        gridTemplateColumns: "34px 68px minmax(0, 1fr)",
        gap: 1.25,
        alignItems: "center",
        p: 1,
        borderRadius: "14px",
        border: "1px solid rgba(15, 23, 42, 0.07)",
        backgroundColor: "rgba(255,255,255,0.94)",
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          color: "#ea580c",
          backgroundColor: "rgba(249, 115, 22, 0.12)",
          fontSize: "0.75rem",
          fontWeight: 800,
        }}
      >
        {rank}
      </Box>
      <Box sx={{ width: 68, height: 68, borderRadius: "10px", overflow: "hidden", backgroundColor: "#f1f5f9" }}>
        {imageUrl ? (
          <Box component="img" src={imageUrl} alt="" onError={(event) => { event.currentTarget.style.display = "none"; }} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : null}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={0.75} alignItems="flex-start">
          <Typography sx={{ flex: 1, minWidth: 0, fontWeight: 750, color: "#202531", fontSize: "0.85rem", lineHeight: 1.3 }}>
            {title}
          </Typography>
          {link ? (
            <IconButton component="a" href={link} target="_blank" rel="noopener noreferrer" size="small" aria-label={`Open ${title}`} sx={{ mt: -0.5 }}>
              <OpenInNewIcon sx={{ fontSize: 16 }} />
            </IconButton>
          ) : null}
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="space-between" sx={{ mt: 0.35 }}>
          <Typography sx={{ minWidth: 0, color: "#64748b", fontSize: "0.72rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {[vendor, productType].filter(Boolean).join(" · ") || id}
          </Typography>
          {rankChangePresentation ? (
            <Tooltip title={rankChangePresentation.tooltip}>
              <Chip
                size="small"
                icon={rankChangePresentation.icon}
                label={rankChangePresentation.label}
                sx={{
                  height: 20,
                  flexShrink: 0,
                  fontSize: "0.65rem",
                  fontWeight: 800,
                  color: rankChangePresentation.color,
                  backgroundColor: rankChangePresentation.backgroundColor,
                  "& .MuiChip-icon": { fontSize: 14, color: rankChangePresentation.color },
                }}
              />
            </Tooltip>
          ) : null}
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ mt: 0.45 }}>
          <Typography sx={{ fontWeight: 800, fontSize: "0.8rem", color: sale ? "#dc2626" : "#202531" }}>{price}</Typography>
          {sale ? <Typography sx={{ color: "#94a3b8", fontSize: "0.7rem", textDecoration: "line-through" }}>{originalPrice}</Typography> : null}
        </Stack>
        <Box role="group" aria-label={`Matched rules for ${title}`} sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5, mt: 0.65 }}>
          <Typography sx={{ color: "#94a3b8", fontSize: "0.65rem", fontWeight: 700 }}>Matched:</Typography>
          {matchedRules.length > 0 ? matchedRules.map((ruleNumber) => {
            const expression = rules?.[ruleNumber - 1]?.expression;
            return (
              <Tooltip key={ruleNumber} title={expression || `Matched merchandising rule ${ruleNumber}`} describeChild>
                <Chip
                  size="small"
                  label={`Rule ${ruleNumber}`}
                  sx={{ height: 18, fontSize: "0.62rem", fontWeight: 750, color: "#7c3aed", backgroundColor: "rgba(124,58,237,0.10)" }}
                />
              </Tooltip>
            );
          }) : <Typography sx={{ color: "#94a3b8", fontSize: "0.65rem" }}>No rules matched</Typography>}
        </Box>
      </Box>
    </Paper>
  );
};

const MerchandisingRulesSection = ({ tenantId, onRuleCountChange }) => {
  const [rules, setRules] = useState([]);
  const [savedRules, setSavedRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [saveFeedback, setSaveFeedback] = useState(null);
  const [serverRuleErrors, setServerRuleErrors] = useState({});
  const [searchPhrase, setSearchPhrase] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState("");

  const endpoint = `/tenant/${encodeURIComponent(tenantId || "")}/merchandising-rules`;

  const loadRules = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setLoadError("");
    setSaveFeedback(null);
    setServerRuleErrors({});
    onRuleCountChange?.(0);
    try {
      const { data } = await axiosInstanceMerchandising.get(endpoint);
      const nextRules = normalizeRules(data);
      setRules(nextRules);
      setSavedRules(nextRules);
      onRuleCountChange?.(nextRules.length);
    } catch (error) {
      setLoadError(getErrorMessage(error, "Failed to load merchandising rules."));
    } finally {
      setLoading(false);
    }
  }, [endpoint, onRuleCountChange, tenantId]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const errors = useMemo(() => validateRules(rules), [rules]);
  const hasErrors = errors.some((ruleErrors) => Object.keys(ruleErrors).length > 0) || Object.keys(serverRuleErrors).length > 0 || rules.length > MAX_RULES;
  const dirty = JSON.stringify(rules) !== JSON.stringify(savedRules);
  const activeCount = rules.filter((rule) => rule.enabled).length;
  const isRuleUnsaved = (rule, index) => JSON.stringify(rule) !== JSON.stringify(savedRules[index]);

  const updateRule = (index, field, value) => {
    setRules((current) => current.map((rule, ruleIndex) => ruleIndex === index ? { ...rule, [field]: value } : rule));
    setSaveFeedback(null);
    setServerRuleErrors({});
  };

  const addRule = () => {
    if (rules.length >= MAX_RULES) return;
    setRules((current) => [...current, { expression: "", boost: "1", enabled: true }]);
    setSaveFeedback(null);
    setServerRuleErrors({});
  };

  const removeRule = (index) => {
    setRules((current) => current.filter((_, ruleIndex) => ruleIndex !== index));
    setSaveFeedback(null);
    setServerRuleErrors({});
  };

  const moveRule = (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= rules.length) return;
    setRules((current) => {
      const reordered = [...current];
      [reordered[index], reordered[nextIndex]] = [reordered[nextIndex], reordered[index]];
      return reordered;
    });
    setSaveFeedback(null);
    setServerRuleErrors({});
  };

  const discardChanges = () => {
    setRules(savedRules.map((rule) => ({ ...rule })));
    setSaveFeedback(null);
    setServerRuleErrors({});
  };

  const saveRules = async () => {
    if (!dirty || hasErrors) return;
    setSaving(true);
    setSaveFeedback(null);
    try {
      const payload = serializeRules(rules);
      const { data } = await axiosInstanceMerchandising.put(endpoint, payload);
      const nextRules = normalizeRules(Array.isArray(data) ? data : payload);
      setRules(nextRules);
      setSavedRules(nextRules);
      setServerRuleErrors({});
      onRuleCountChange?.(nextRules.length);
      setSaveFeedback({ severity: "success", message: "Merchandising rules saved successfully." });
    } catch (error) {
      const message = getErrorMessage(error, "Failed to save merchandising rules.");
      const invalidRuleMatch = message.match(/Rule (\d+) expression could not be validated/i);
      if (invalidRuleMatch) {
        const ruleIndex = Number(invalidRuleMatch[1]) - 1;
        setServerRuleErrors({ [ruleIndex]: "Expression could not be validated against the tenant product index." });
      }
      setSaveFeedback({ severity: "error", message });
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async () => {
    if (!searchPhrase.trim() || hasErrors) return;
    setPreviewing(true);
    setPreviewError("");
    try {
      const { data } = await axiosInstanceMerchandisingPreview.post("/Products/merchandising-preview", {
        searchPhrase: searchPhrase.trim(),
        rules: serializeRules(rules),
      }, {
        headers: { tenantId },
      });
      setServerRuleErrors({});
      setPreview(data || {});
    } catch (error) {
      const message = getErrorMessage(error, "Failed to preview merchandising rules.");
      const invalidRuleMatch = message.match(/Rule (\d+) expression is invalid/i);
      if (invalidRuleMatch) {
        const ruleIndex = Number(invalidRuleMatch[1]) - 1;
        setServerRuleErrors({ [ruleIndex]: "Expression could not be validated against the tenant product index." });
      }
      setPreview(null);
      setPreviewError(message);
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) {
    return (
      <Stack alignItems="center" spacing={1.5} sx={{ py: 8 }}>
        <CircularProgress size={30} sx={{ color: "#f97316" }} />
        <Typography sx={{ color: "#64748b" }}>Loading merchandising rules…</Typography>
      </Stack>
    );
  }

  if (loadError) {
    return <Alert severity="error" action={<Button color="inherit" size="small" onClick={loadRules}>Retry</Button>}>{loadError}</Alert>;
  }

  const products = readValue(preview, "products", "Products") || [];
  const total = readValue(preview, "total", "Total");
  const hasMore = readValue(preview, "hasMore", "HasMore");
  const searchLevel = readValue(preview, "searchLevel", "SearchLevel");

  return (
    <Stack spacing={2}>
      <Alert severity="warning" sx={{ borderRadius: "14px", alignItems: "center" }}>
        Rules adjust the score of matching products without filtering normal results. When at least one rule is enabled, normal LLM reranking is bypassed.
      </Alert>

      {rules.length > MAX_RULES ? <Alert severity="error">A maximum of {MAX_RULES} merchandising rules is allowed.</Alert> : null}
      {saveFeedback ? <Alert severity={saveFeedback.severity} sx={{ borderRadius: "12px" }}>{saveFeedback.message}</Alert> : null}

      <Paper elevation={0} sx={{ p: 1.5, borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", backgroundColor: "rgba(255,255,255,0.96)" }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between">
          <Typography variant="body2" sx={{ color: dirty ? "#ea580c" : "#64748b", fontWeight: 700 }}>
            {dirty ? "You have unsaved merchandising changes." : "All merchandising changes are saved."}
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button startIcon={<UndoIcon />} variant="outlined" onClick={discardChanges} disabled={!dirty || saving} sx={neutralButtonSx}>Discard</Button>
            <Button startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <SaveOutlinedIcon />} variant="contained" onClick={saveRules} disabled={!dirty || hasErrors || saving} sx={primaryButtonSx}>{saving ? "Saving…" : "Save rules"}</Button>
          </Stack>
        </Stack>
      </Paper>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "minmax(0, 7fr) minmax(340px, 5fr)" }, gap: 2, alignItems: "start" }}>
        <Stack spacing={1.5}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ fontWeight: 800, color: "#202531" }}>Rules</Typography>
              <Chip size="small" label={`${activeCount} active · ${rules.length} total`} sx={{ fontWeight: 700, backgroundColor: "rgba(249,115,22,0.12)", color: "#ea580c" }} />
              <Typography variant="caption" sx={{ color: "#94a3b8" }}>{rules.length} / {MAX_RULES}</Typography>
            </Stack>
            <Button startIcon={<AddCircleOutlineIcon />} variant="outlined" size="small" onClick={addRule} disabled={rules.length >= MAX_RULES} sx={neutralButtonSx}>
              Add rule
            </Button>
          </Stack>

          {rules.length === 0 ? (
            <Paper elevation={0} sx={{ p: 5, textAlign: "center", borderRadius: "16px", border: "1px dashed rgba(249,115,22,0.35)", backgroundColor: "rgba(255,247,241,0.45)" }}>
              <Typography sx={{ fontWeight: 800, color: "#202531" }}>No merchandising rules yet</Typography>
              <Typography variant="body2" sx={{ mt: 0.5, mb: 2, color: "#64748b" }}>Add a rule to boost products that match a Lucene expression.</Typography>
              <Button startIcon={<AddCircleOutlineIcon />} variant="contained" onClick={addRule} sx={primaryButtonSx}>Add first rule</Button>
            </Paper>
          ) : rules.map((rule, index) => (
            <Paper
              key={index}
              data-testid={`merchandising-rule-${index}`}
              data-unsaved={isRuleUnsaved(rule, index)}
              elevation={0}
              sx={{
                p: 1.75,
                borderRadius: "16px",
                border: isRuleUnsaved(rule, index) ? "1px solid rgba(249,115,22,0.42)" : "1px solid rgba(15,23,42,0.08)",
                backgroundColor: isRuleUnsaved(rule, index) ? "rgba(255,237,213,0.78)" : "rgba(255,255,255,0.82)",
                boxShadow: isRuleUnsaved(rule, index) ? "inset 3px 0 0 #f97316" : "none",
                transition: "background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ fontWeight: 800, color: "#202531", fontSize: "0.86rem" }}>Rule {index + 1}</Typography>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={<Switch size="small" checked={rule.enabled} onChange={(event) => updateRule(index, "enabled", event.target.checked)} inputProps={{ "aria-label": `Rule ${index + 1} enabled` }} sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#f97316" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#f97316" } }} />}
                    label={<Typography variant="caption" sx={{ color: rule.enabled ? "#15803d" : "#64748b", fontWeight: 700 }}>{rule.enabled ? "Enabled" : "Disabled"}</Typography>}
                  />
                </Stack>
                <Stack direction="row" spacing={0.25}>
                  <Tooltip title="Move up"><span><IconButton size="small" disabled={index === 0} onClick={() => moveRule(index, -1)} aria-label={`Move rule ${index + 1} up`}><ArrowUpwardIcon sx={{ fontSize: 17 }} /></IconButton></span></Tooltip>
                  <Tooltip title="Move down"><span><IconButton size="small" disabled={index === rules.length - 1} onClick={() => moveRule(index, 1)} aria-label={`Move rule ${index + 1} down`}><ArrowDownwardIcon sx={{ fontSize: 17 }} /></IconButton></span></Tooltip>
                  <Tooltip title="Delete rule"><IconButton size="small" onClick={() => removeRule(index)} aria-label={`Delete rule ${index + 1}`} sx={{ color: "#dc2626" }}><DeleteOutlineIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                </Stack>
              </Stack>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) 140px" }, gap: 1.5, alignItems: "start" }}>
                <TextField
                  label="Lucene expression"
                  value={rule.expression}
                  onChange={(event) => updateRule(index, "expression", event.target.value)}
                  multiline
                  minRows={2}
                  error={Boolean(errors[index]?.expression || serverRuleErrors[index])}
                  helperText={errors[index]?.expression || serverRuleErrors[index] || `${rule.expression.length} / ${MAX_EXPRESSION_LENGTH}`}
                  inputProps={{ "aria-label": `Rule ${index + 1} expression`, maxLength: MAX_EXPRESSION_LENGTH + 1 }}
                  sx={{ ...inputSx, "& textarea": { fontFamily: "'Roboto Mono', ui-monospace, monospace", fontSize: "0.84rem" } }}
                />
                <TextField
                  label="Boost"
                  type="number"
                  value={rule.boost}
                  onChange={(event) => updateRule(index, "boost", event.target.value)}
                  error={Boolean(errors[index]?.boost)}
                  helperText={errors[index]?.boost || `${MIN_BOOST} to ${MAX_BOOST}`}
                  inputProps={{ "aria-label": `Rule ${index + 1} boost`, min: MIN_BOOST, max: MAX_BOOST, step: 0.1 }}
                  sx={inputSx}
                />
              </Box>
            </Paper>
          ))}
        </Stack>

        <Paper elevation={0} sx={{ p: 2, borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", backgroundColor: "rgba(248,250,252,0.86)", position: { xl: "sticky" }, top: { xl: 16 } }}>
          <Typography sx={{ fontWeight: 800, color: "#202531" }}>Search preview</Typography>
          <Typography variant="body2" sx={{ mt: 0.35, mb: 1.5, color: "#64748b" }}>Test the current unsaved rules against the tenant’s product search.</Typography>
          <Stack direction={{ xs: "column", sm: "row", xl: "column" }} spacing={1}>
            <TextField
              fullWidth
              size="small"
              label="Search phrase"
              value={searchPhrase}
              onChange={(event) => setSearchPhrase(event.target.value)}
              onKeyDown={(event) => { if (event.key === "Enter") runPreview(); }}
              sx={inputSx}
            />
            <Button startIcon={previewing ? <CircularProgress size={15} color="inherit" /> : <PlayArrowIcon />} variant="contained" onClick={runPreview} disabled={previewing || !searchPhrase.trim() || hasErrors} sx={{ ...primaryButtonSx, whiteSpace: "nowrap" }}>
              {previewing ? "Running…" : "Run preview"}
            </Button>
          </Stack>
          {hasErrors ? <Typography variant="caption" sx={{ display: "block", mt: 1, color: "#b91c1c" }}>Resolve rule errors before previewing.</Typography> : null}
          {previewError ? <Alert severity="error" sx={{ mt: 1.5, borderRadius: "12px" }}>{previewError}</Alert> : null}

          {preview ? (
            <Stack spacing={1.25} sx={{ mt: 2 }}>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`${total ?? products.length} results`} />
                {searchLevel !== undefined ? <Chip size="small" label={`Search level ${searchLevel}`} /> : null}
                {hasMore ? <Chip size="small" label="More available" /> : null}
              </Stack>
              {products.length > 0 ? products.map((product, index) => <ProductTile key={readValue(product, "id", "Id") || index} product={product} rank={index + 1} rules={rules} />) : (
                <Typography variant="body2" sx={{ py: 3, textAlign: "center", color: "#64748b" }}>No products matched this search.</Typography>
              )}
            </Stack>
          ) : !previewError ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <PlayArrowIcon sx={{ color: "#cbd5e1", fontSize: 38 }} />
              <Typography variant="body2" sx={{ color: "#94a3b8" }}>Preview results will appear here.</Typography>
            </Box>
          ) : null}
        </Paper>
      </Box>

    </Stack>
  );
};

export default MerchandisingRulesSection;
